import React, {useState, useEffect} from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Modal,
  Stack,
  Alert,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {PencilLine} from 'lucide-react';
import {useAuthFrontendApis} from '@propelauth/frontend-apis-react';
import {useAuthInfo} from '@propelauth/react';
import {useTheme} from '@mui/material/styles';
import {SettingsCard} from '../../components/SettingsCard';

const styles = {
  container: {
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  card: {
    marginBottom: '24px',
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: 600,
    marginBottom: '24px',
    color: '#162523',
  },
  fieldContainer: {
    marginBottom: '16px',
  },
  buttonContainer: {
    display: 'flex',
    gap: '12px',
    marginTop: '16px',
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '0.5rem',
      height: '2.5rem',
      '&:hover fieldset': {
        borderColor: '#26A993',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#26A993',
      },
    },
  },
  fieldLabel: {
    fontSize: '0.875rem',
    fontWeight: '500',
    lineHeight: '140%',
    color: '#043B34',
    marginBottom: '0.5rem',
  },
  modal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    borderRadius: '8px',
    boxShadow: 24,
    p: 4,
  },
};

const UserDetails = () => {
  const {updateUserMetadata, updateEmail, updatePassword} =
    useAuthFrontendApis();
  const {user} = useAuthInfo();
  const theme = useTheme();

  // Basic settings state
  const [basicSettings, setBasicSettings] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });
  const [basicSettingsEditing, setBasicSettingsEditing] = useState({
    firstName: false,
    lastName: false,
    email: false,
  });
  const [basicSettingsLoading, setBasicSettingsLoading] = useState(false);

  // Password modal state
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
  });
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Email update requires password
  const [emailPassword, setEmailPassword] = useState('');

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success', // 'success' or 'error'
  });

  useEffect(() => {
    if (user) {
      setBasicSettings({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
      });
    }
  }, [user]);

  const handleBasicSettingChange = (field, value) => {
    setBasicSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const startEditing = (field) => {
    setBasicSettingsEditing((prev) => ({
      ...prev,
      [field]: true,
    }));
  };

  const cancelEditing = (field) => {
    setBasicSettingsEditing((prev) => ({
      ...prev,
      [field]: false,
    }));
    // Reset to original value
    if (user) {
      setBasicSettings((prev) => ({
        ...prev,
        [field]:
          field === 'firstName'
            ? user.firstName || ''
            : field === 'lastName'
              ? user.lastName || ''
              : user.email || '',
      }));
    }
    if (field === 'email') {
      setEmailPassword('');
    }
  };

  const saveBasicSetting = async (field) => {
    setBasicSettingsLoading(true);

    try {
      if (field === 'email') {
        const response = await updateEmail({
          new_email: basicSettings.email,
          password: emailPassword,
        });

        response.handle({
          success: () => {
            setBasicSettingsEditing((prev) => ({...prev, [field]: false}));
            setEmailPassword('');
            setSnackbar({
              open: true,
              message:
                'We sent you a confirmation email to your new email address.',
              severity: 'success',
            });
          },
          badRequest: (error) => {
            setSnackbar({
              open: true,
              message:
                error.user_facing_errors.new_email || 'Invalid email format',
              severity: 'error',
            });
          },
          failedToSendEmail: () => {
            setSnackbar({
              open: true,
              message: 'There was an error sending the confirmation email',
              severity: 'error',
            });
          },
          incorrectPassword: () => {
            setSnackbar({
              open: true,
              message: 'Incorrect password, please try again',
              severity: 'error',
            });
          },
          userAccountLocked: () => {
            setSnackbar({
              open: true,
              message: 'Your account is locked, please log out and try again',
              severity: 'error',
            });
          },
          rateLimit: () => {
            setSnackbar({
              open: true,
              message: 'Please wait a moment before trying again',
              severity: 'error',
            });
          },
          emailChangeDisabled: () => {
            setSnackbar({
              open: true,
              message: 'Email change is disabled for your account',
              severity: 'error',
            });
          },
          cannotChangeEmailDueToOrgMembership: () => {
            setSnackbar({
              open: true,
              message:
                'Invalid domain. Please update to a domain that is enabled by your organizations',
              severity: 'error',
            });
          },
          unexpectedOrUnhandled: (error) => {
            setSnackbar({
              open: true,
              message: error.user_facing_error,
              severity: 'error',
            });
          },
        });
      } else {
        // Update first name or last name
        const updateData = {};
        if (field === 'firstName') {
          updateData.first_name = basicSettings.firstName;
        } else if (field === 'lastName') {
          updateData.last_name = basicSettings.lastName;
        }

        const response = await updateUserMetadata(updateData);

        await response.handle({
          success: () => {
            setBasicSettingsEditing((prev) => ({...prev, [field]: false}));
            setSnackbar({
              open: true,
              message: `${field === 'firstName' ? 'First name' : 'Last name'} updated successfully`,
              severity: 'success',
            });
          },
          badRequest: (error) => {
            const userFacingErrors = Object.entries(error.user_facing_errors)
              .map(([fieldName, fieldErrorMessage]) => `${fieldErrorMessage}`)
              .join('; ');
            setSnackbar({
              open: true,
              message: userFacingErrors,
              severity: 'error',
            });
          },
          unexpectedOrUnhandled: (error) => {
            setSnackbar({
              open: true,
              message: error.user_facing_error,
              severity: 'error',
            });
          },
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'An unexpected error occurred',
        severity: 'error',
      });
    }

    setBasicSettingsLoading(false);
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);

    const response = await updatePassword({
      current_password: passwordData.currentPassword,
      password: passwordData.newPassword,
    });

    response.handle({
      success: () => {
        setSnackbar({
          open: true,
          message: 'Password updated successfully',
          severity: 'success',
        });
        setPasswordData({currentPassword: '', newPassword: ''});
        setPasswordModalOpen(false);
      },
      incorrectPassword: () => {
        setSnackbar({
          open: true,
          message: 'The current password is incorrect. Please try again.',
          severity: 'error',
        });
      },
      userAccountLocked: () => {
        setSnackbar({
          open: true,
          message: 'Your account is locked, unable to update password.',
          severity: 'error',
        });
      },
      badRequest: (error) => {
        setSnackbar({
          open: true,
          message:
            error.user_facing_errors.password || 'Invalid password format',
          severity: 'error',
        });
      },
      unexpectedOrUnhandled: (error) => {
        setSnackbar({
          open: true,
          message: error.user_facing_error,
          severity: 'error',
        });
      },
    });

    setPasswordLoading(false);
  };

  if (!user) {
    return (
      <Box sx={styles.container}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      {/* Page Title */}
      <Typography
        sx={{
          fontSize: '26px',
          fontWeight: 700,
          lineHeight: '130%',
          letterSpacing: '-0.24px',
          color: '#162523',
          marginBottom: '32px',
        }}
      >
        My Account
      </Typography>

      {/* Basic Settings Card */}
      <SettingsCard sx={styles.card}>
        <Typography sx={styles.cardTitle}>Basic settings</Typography>

        {/* Grid Container for Fields */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
              md: '1fr 1fr 1fr',
            },
            gap: '32px',
            width: '100%',
          }}
        >
          {/* First Name */}
          <Box sx={styles.fieldContainer}>
            <Typography sx={styles.fieldLabel}>First Name</Typography>
            <TextField
              value={basicSettings.firstName}
              onChange={(e) =>
                handleBasicSettingChange('firstName', e.target.value)
              }
              onClick={() =>
                !basicSettingsEditing.firstName && startEditing('firstName')
              }
              disabled={basicSettingsLoading}
              fullWidth
              variant="outlined"
              sx={styles.textField}
              inputProps={{
                autoComplete: 'new-password',
                'data-1p-ignore': true,
                'data-lpignore': true,
                'data-form-type': 'other',
                name: 'first-name',
                type: 'search',
              }}
            />
            {basicSettingsEditing.firstName && (
              <Box sx={styles.buttonContainer}>
                <Button
                  onClick={() => saveBasicSetting('firstName')}
                  disabled={basicSettingsLoading}
                  sx={theme.button.primary.small}
                >
                  {basicSettingsLoading ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  onClick={() => cancelEditing('firstName')}
                  disabled={basicSettingsLoading}
                  sx={theme.button.outline.small}
                >
                  Cancel
                </Button>
              </Box>
            )}
          </Box>

          {/* Last Name */}
          <Box sx={styles.fieldContainer}>
            <Typography sx={styles.fieldLabel}>Last Name</Typography>
            <TextField
              value={basicSettings.lastName}
              onChange={(e) =>
                handleBasicSettingChange('lastName', e.target.value)
              }
              onClick={() =>
                !basicSettingsEditing.lastName && startEditing('lastName')
              }
              disabled={basicSettingsLoading}
              fullWidth
              variant="outlined"
              sx={styles.textField}
              inputProps={{
                autoComplete: 'new-password',
                'data-1p-ignore': true,
                'data-lpignore': true,
                'data-form-type': 'other',
                name: 'last-name',
                type: 'search',
              }}
            />
            {basicSettingsEditing.lastName && (
              <Box sx={styles.buttonContainer}>
                <Button
                  onClick={() => saveBasicSetting('lastName')}
                  disabled={basicSettingsLoading}
                  sx={theme.button.primary.small}
                >
                  {basicSettingsLoading ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  onClick={() => cancelEditing('lastName')}
                  disabled={basicSettingsLoading}
                  sx={theme.button.outline.small}
                >
                  Cancel
                </Button>
              </Box>
            )}
          </Box>

          {/* Email */}
          <Box sx={styles.fieldContainer}>
            <Typography sx={styles.fieldLabel}>Email</Typography>
            <TextField
              value={basicSettings.email}
              onChange={(e) =>
                handleBasicSettingChange('email', e.target.value)
              }
              onClick={() =>
                !basicSettingsEditing.email && startEditing('email')
              }
              disabled={basicSettingsLoading}
              fullWidth
              variant="outlined"
              type="email"
              sx={styles.textField}
              inputProps={{
                autoComplete: 'new-password',
                'data-1p-ignore': true,
                'data-lpignore': true,
                'data-form-type': 'other',
                name: 'user-email',
                type: 'search',
              }}
            />
            {basicSettingsEditing.email && (
              <>
                <Box sx={{marginTop: '12px'}}>
                  <Typography sx={styles.fieldLabel}>
                    Confirm Password
                  </Typography>
                  <TextField
                    type="password"
                    value={emailPassword}
                    onChange={(e) => setEmailPassword(e.target.value)}
                    disabled={basicSettingsLoading}
                    fullWidth
                    variant="outlined"
                    sx={styles.textField}
                    placeholder="Enter your current password to update email"
                  />
                </Box>
                <Box sx={styles.buttonContainer}>
                  <Button
                    onClick={() => saveBasicSetting('email')}
                    disabled={basicSettingsLoading || !emailPassword}
                    sx={theme.button.primary.small}
                  >
                    {basicSettingsLoading ? 'Updating...' : 'Update Email'}
                  </Button>
                  <Button
                    onClick={() => cancelEditing('email')}
                    disabled={basicSettingsLoading}
                    sx={theme.button.outline.small}
                  >
                    Cancel
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </SettingsCard>

      {/* Security Settings Card */}
      <SettingsCard sx={styles.card}>
        <Typography sx={styles.cardTitle}>Security settings</Typography>

        <Box sx={styles.fieldContainer}>
          <Button
            onClick={() => setPasswordModalOpen(true)}
            fullWidth
            sx={{
              ...theme.button.primary.large,
              justifyContent: 'flex-start',
              width: '100%',
            }}
          >
            <PencilLine size={16} style={{marginRight: '8px'}} />
            Change password
          </Button>
        </Box>
      </SettingsCard>

      {/* Password Update Modal */}
      <Modal
        open={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
        aria-labelledby="password-modal-title"
      >
        <Box sx={styles.modal}>
          <Typography
            id="password-modal-title"
            variant="h6"
            component="h2"
            sx={{marginBottom: '24px'}}
          >
            Change password
          </Typography>

          <form onSubmit={handlePasswordUpdate}>
            <Stack spacing={2}>
              <Box>
                <Typography sx={styles.fieldLabel}>Current Password</Typography>
                <TextField
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) =>
                    setPasswordData((prev) => ({
                      ...prev,
                      currentPassword: e.target.value,
                    }))
                  }
                  disabled={passwordLoading}
                  required
                  fullWidth
                  sx={styles.textField}
                />
              </Box>
              <Box>
                <Typography sx={styles.fieldLabel}>New Password</Typography>
                <TextField
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) =>
                    setPasswordData((prev) => ({
                      ...prev,
                      newPassword: e.target.value,
                    }))
                  }
                  disabled={passwordLoading}
                  required
                  fullWidth
                  sx={styles.textField}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end',
                  marginTop: '24px',
                }}
              >
                <Button
                  onClick={() => setPasswordModalOpen(false)}
                  disabled={passwordLoading}
                  sx={theme.button.outline.medium}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    passwordLoading ||
                    !passwordData.currentPassword ||
                    !passwordData.newPassword
                  }
                  sx={theme.button.primary.medium}
                >
                  {passwordLoading ? 'Updating...' : 'Update Password'}
                </Button>
              </Box>
            </Stack>
          </form>
        </Box>
      </Modal>

      {/* Snackbar for success and error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({...prev, open: false}))}
        anchorOrigin={{vertical: 'top', horizontal: 'center'}}
        style={{marginTop: '35px'}}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({...prev, open: false}))}
          severity={snackbar.severity}
          icon={
            snackbar.severity === 'success' ? (
              <Box
                sx={{
                  backgroundColor: '#26A993',
                  borderRadius: '50%',
                  width: 22,
                  height: 22,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <CheckIcon sx={{color: 'white', fontSize: 16}} />
              </Box>
            ) : (
              <ErrorOutlineIcon sx={{color: '#D73E3E', fontSize: 22}} />
            )
          }
          sx={{
            width: '100%',
            backgroundColor: '#FFFFFF',
            color: '#000000',
            border: '1px solid #E1E5E4',
            borderRadius: '8px',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserDetails;
